import React from 'react'

import { Link } from 'react-router-dom'

import { Box, BoxProps, Card, CardProps, LinearProgress, makeAppStyles, Tooltip } from '@percept/mui'

import { InfoOutlined, OpenInNew } from '@percept/mui/icons'

import { Palette, PaletteColor } from '@material-ui/core/styles/createPalette'

import { TypedKeys } from '@percept/types'

import { cardLoadingStyles } from '@percept/app-components'


type CardColor = TypedKeys<Palette, PaletteColor>

export type DashboardCardProps = (
  Omit<CardProps, 'title'> & {
    title: React.ReactNode
    secondaryTitleContent?: React.ReactNode
    tooltipContent?: React.ReactNode
    color?: CardColor
    route?: string
    isRefetching?: boolean
    loaderColor?: 'primary' | 'secondary'
  } & ({
    route?: string
    externalLink?: never
  } | {
    route?: never
    externalLink?: string
  })
)

const useStyles = makeAppStyles<{ color?: CardColor }>( theme => ({
  ...cardLoadingStyles,
  card: {
    ...cardLoadingStyles.card,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  cardHeader: ({ color = 'secondary' }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(5.5),
    color: theme.palette[color].contrastText,
    backgroundColor: theme.palette[color].main,
    fontSize: 15,
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('lg')]: {
      minHeight: theme.spacing(5),
    },
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
      minHeight: theme.spacing(4.5),
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 13,
      minHeight: theme.spacing(4.25),
      padding: theme.spacing(0, 1.5),
    },
  }),
  link: {
    display: 'flex',
    alignItems: 'center',
  },
  linkIcon: {
    marginRight: theme.spacing(0.75),
  },
  tooltipIcon: {
    opacity: 0.75,
    marginLeft: theme.spacing(1.5),
  },
  tooltip: {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.getContrastText(theme.palette.action.disabledBackground),
    fontSize: 13,
    padding: theme.spacing(1),
    boxShadow: theme.shadows[3],
  },
  secondaryTitleContent: {
    marginLeft: 'auto',
  },
}))

export const DashboardCard = ({
  title,
  secondaryTitleContent,
  tooltipContent,
  color,
  route,
  externalLink,
  isRefetching,
  loaderColor = 'primary',
  children,
  ...props
}: DashboardCardProps): JSX.Element => {
  const classes = useStyles({ color })
  return (
    <Card {...props}>
      <div className={classes.cardHeader} style={{backgroundColor: color}}>
        { route ? (
          <Link className={classes.link} to={route}>
            <OpenInNew className={classes.linkIcon} fontSize='small' />
            { title }
          </Link>
        ) : externalLink ? (
          <a className={classes.link} href={externalLink} rel='noopener noreferrer' target='_blank'>
            <OpenInNew className={classes.linkIcon} fontSize='small' />
            { title }
          </a>
        ) : title }
        { tooltipContent && (
          <Tooltip
            classes={{
              tooltip: classes.tooltip,
            }}
            title={tooltipContent}>
            <InfoOutlined className={classes.tooltipIcon} fontSize='small' />
          </Tooltip>
        )}
        { secondaryTitleContent && (
          <div className={classes.secondaryTitleContent}>
            { secondaryTitleContent }
          </div>
        ) }
      </div>
      <div className={classes.card}>
        { isRefetching && (
          <LinearProgress className={classes.refetchIndicator} color={loaderColor} variant='indeterminate' />
        )}
        { children }
      </div>
    </Card>
  )
}


export const DashboardCardFallbackContent = (props: BoxProps) => (
  <Box
    display='flex'
    flexGrow={1}
    justifyContent='center'
    alignItems='center'
    p={3}
    fontSize={16}
    color='text.secondary'
    {...props} />
)
